<template>
  <div style="position: relative;" class="bg-white p-4">
    <div>
      <div class="d-flex justify-content-between" style="margin-bottom: 1.70rem;">
        <router-link to="/management/question-set">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;">
            <path
              d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
              stroke="#FF7C48"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </router-link>
        <div class="d-flex align-items-center" style="gap: 2.34rem;">
          <div>
            <h1 class="title">{{ $route.params.id ? $t("question-set.edit") : $t("question-set.new") }}</h1>
          </div>
        </div>
        <div class="d-flex align-items-center" @click="questionModal = true">
          <div class="new-task-text">{{ $t("question.new") }}</div>
          <div class="plus-button" style="margin-right: 1.56rem;"></div>
        </div>
      </div>

      <div class="d-flex flex-shrink-0" style="gap: 1.87rem; margin-bottom: 2.37rem;">
        <div style="width: 33.75rem;">
          <sweet-input v-model="form.name" :isValid="!$v.form.name.$error" @input="$v.form.name.$touch()" layout="vertical" :label="$t('question-set.name')" class="mb-3" />

          <label>{{ $t("question-set.products") }}</label>

          <b-form-tags v-model="form.products" size="lg" class="mb-2" add-on-change no-outer-focus>
            <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
              <b-form-select v-bind="inputAttrs" v-on="inputHandlers" :disabled="disabled || !scopedProducts || scopedProducts.length === 0" :options="localizedScopedProducts">
                <template #first>
                  <!-- This is required to prevent bugs with Safari -->
                  <option disabled value=""> {{ $t("product.text") }}</option>
                </template>
              </b-form-select>

              <ul v-if="tags.length > 0" class="list-inline d-inline-block mt-2">
                <li v-for="tag in tags" :key="tag" class="list-inline-item">
                  <b-form-tag @remove="removeTag(tag)" :title="tag" :disabled="disabled" variant="primary">{{ $t("product." + tag.toLowerCase() + ".text") }}</b-form-tag>
                </li>
              </ul>
              <b-form-text v-else>
                <span class="text-danger">{{ $t("product.required") }}</span>
              </b-form-text>
            </template>
          </b-form-tags>
        </div>
        <div style="width: calc(100% - 33.75rem);">
          <sweet-textarea
            class="mt-3"
            v-model="form.description"
            :isValid="!$v.form.description.$error"
            @input="$v.form.description.$touch()"
            type="text"
            layout="vertical"
            :placeholder="$t('question-set.description')"
            :label="$t('question-set.description')"
            :rows="7"
          />
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-between mb-4">
        <div class="d-flex" style="gap: 2.37rem">
          <div class="pswitch">
            <div class="text">
              {{ $t("question-set.is-scoring-active") }}
            </div>
            <b-form-checkbox switch v-model="form.isScoringActive" :unchecked-value="false" class="custom-checkbox mb-3"></b-form-checkbox>
          </div>
          <div v-if="form.isScoringActive" class="pswitch">
            <div class="text">
              {{ $t("question-set.is-rating-active") }}
            </div>
            <b-form-checkbox switch v-model="form.isRatingActive" :unchecked-value="false" class="custom-checkbox mb-3"></b-form-checkbox>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <div class="totalq">{{ $t("question-set.question-count") }}:</div>
          <div class="totalq-number" v-if="form.questions">{{ form.questions.length }}</div>
          <div class="totalq">{{ $t("question-set.total-point") }}:</div>
          <div class="totalq-number">{{ totalQuestionPoint }}</div>
          <button class="btn btn-main" :disabled="!isSaveButtonEnabled" @click="save" style="width: 12.125rem;">
            {{ $t("question-set.save") }}
          </button>
        </div>
      </div>

      <div v-for="questionWrapper in form.questions" :key="questionWrapper._id" class="question question-wbg">
        <div>
          <div class="text-center hand" @click="orderUp(questionWrapper)"><i class="font-size-24 text-muted bx bx-chevron-up"></i></div>
          <div class="number bg-secondary">{{ questionWrapper.order }}</div>
          <div class="text-center hand" @click="orderDown(questionWrapper)"><i class="font-size-24 text-muted bx bx-chevron-down"></i></div>

          <div class="mt-4 pl-2">
            <i
              class="bx bx-trash text-danger hand font-size-24"
              v-b-tooltip.hover
              :title="$t('question-set.remove-question')"
              @click="removeQuestionFromQuestionSet(questionWrapper.question)"
            ></i>

            <i @click="goToQuestionEdit(questionWrapper.question)" class="bx bx-pencil font-size-24 text-warning hand my-4" v-b-tooltip.hover :title="$t('common.edit')"></i>
          </div>
        </div>
        <div>
          <div class="text">
            {{ questionWrapper.question.name | translate }}
          </div>

          <div class="text-muted text-smaller">
            (
            <span v-if="questionWrapper.question.answerType == 'SINGLE'">{{ $t("question.answer-type.single") }}</span>
            <span v-if="questionWrapper.question.answerType == 'MULTIPLE'">{{ $t("question.answer-type.multiple") }}</span>
            <span v-if="questionWrapper.question.answerType == 'FREE_TEXT'">{{ $t("question.answer-type.free-text") }}</span>
            )
          </div>
          <div>
            <question-category-select :label="$t('question.category.fwsed')" class="mt-2" :selected="questionWrapper.question.fwsedCategory" :disabled="true" />
            <div class="pt-2 ml-2 text-main" v-if="questionWrapper.question.fileRequired">{{ $t("question.file-required") }}</div>
          </div>
          <div v-if="questionWrapper.question.answerType != 'FREE_TEXT'" class="answers">
            <ul>
              <li v-for="option in questionWrapper.question.options" :key="option._id" class="mb-2">
                <span class="font-size-15"> {{ option.name | translate }}</span>
                <div v-if="form.isScoringActive">
                  <div class="d-flex   text-muted" style="gap:1rem">
                    <div class="float-left">{{ $t("common.point") }} :</div>
                    <div>
                      <input v-model="option.score" class="form-control form-control-sm" type="number" min="0" max="90" />
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div v-if="questionWrapper.question.answerType != 'FREE_TEXT'" class="card card-body" style="min-width:600px">
            <h5>{{ $t("question-set.conditional.title") }}</h5>

            <div v-for="conditional in renderConditionals(questionWrapper)" :key="conditional.optionId">
              <i class="bx bx-trash text-danger hand" @click="removeConditional(questionWrapper, conditional)"></i>

              {{ conditional.option.name | translate }} ->
              {{ conditional.question.name | translate }}
            </div>

            <b-row class="mt-2">
              <b-col>
                <h6>{{ $t("question.option.text") }}</h6>

                <select v-model="questionWrapper.conditionalQuestionOptionChoose" class="form-control form-control-sm">
                  <option v-for="option in questionWrapper.question.options" :key="option._id" :value="option._id">
                    {{ option.name | translate }}
                  </option>
                </select>
              </b-col>
              <b-col>
                <h6>{{ $t("question.text") }}</h6>

                <select v-model="questionWrapper.conditionalQuestionOtherQuestionChoose" class="form-control form-control-sm">
                  <option
                    v-for="(questionWrapper2, index) in form.questions.filter((i) => i.question._id != questionWrapper.question._id)"
                    :key="`${index}${questionWrapper2.question._id}`"
                    :value="questionWrapper2.question._id"
                  >
                    {{ questionWrapper2.question.name | translate }}
                  </option>
                </select>
              </b-col>
              <b-col class="pt-3">
                <button
                  :disabled="!questionWrapper.conditionalQuestionOtherQuestionChoose || !questionWrapper.conditionalQuestionOptionChoose"
                  @click="addConditionalQuestion(questionWrapper)"
                  class="btn btn-sm btn-secondary"
                >
                  {{ $t("common.add") }}
                </button>
              </b-col>
            </b-row>
          </div>
        </div>
        <div v-if="questionWrapper.question.answerType != 'FREE_TEXT'" class="points">
          <i class="iconoir-medal"></i>
          <div>
            {{ totalQuestionOptionScore(questionWrapper) }} <br />
            {{ $t("common.point") }}
          </div>
        </div>
      </div>

      <div v-if="form.isRatingActive">
        <hr />

        <div class="rd-card">
          <div class="rd-title">{{ $t("question-set.rating-table-assesment") }}</div>
          <template v-for="(rating, index) in form.ratingTable">
            <div :key="(rating.id ? rating.id : 'id') + index + '1'" v-if="true">
              <sweet-input
                :isValid="!!rating.name"
                v-model="rating.name"
                @input="$v.form.$touch()"
                layout="vertical"
                :label="$t('question-set.rating-table-assesment')"
                style="width: 10.625rem;"
                class="mb-3"
              />
              <h6>{{ $t("common.min") }}</h6>
              <input v-model="rating.min" @input="$v.form.$touch()" type="number" layout="vertical" class="form-control form-control-sm mb-3" style="width: 10.625rem;" />
              <h6>{{ $t("common.max") }}</h6>
              <input v-model="rating.max" @input="$v.form.$touch()" type="number" layout="vertical" class="form-control form-control-sm mb-3" style="width: 10.625rem;" />

              <i class="iconoir-trash rd-icon" @click="removeRating(index)"></i>
            </div>

            <div class="line line2" :key="rating.name + index + '2'" v-if="true"></div>
          </template>

          <div class="d-flex align-items-center mt-3">
            <button class="button add-btn" @click="appendRating"><i class="iconoir-plus"></i> {{ $t("question-set.add-rating") }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- BEGIN : FORM -->
    <div class="backdrop2" :class="{ active: questionModal }" @click="questionModal = false">
      <div class="modal2" :style="{ maxWidth: activeModalTab === 'library' ? '81.5625rem' : '56.375rem' }" @click.stop>
        <nav class="tab-menu mx-auto mb-4">
          <div :class="{ active: activeModalTab === 'library' }" @click="activeModalTab = 'library'">
            {{ $t("common.add-from-library") }}
          </div>
          <div :class="{ active: activeModalTab === 'newquestion' }" @click="activeModalTab = 'newquestion'">
            {{ $t("question.new") }}
          </div>
        </nav>

        <div class="tab-content">
          <div :class="{ active: activeModalTab === 'library' }">
            <content-loading v-if="isLibraryLoading" />
            <empty v-if="!isLibraryLoading && (!libraryPagination || libraryPagination.docs.length == 0)" width="100px" />
            <div v-if="libraryPagination">
              <div class="question question-wbg" v-for="question in libraryPagination.docs" :key="question._id">
                <i
                  v-if="isQuestionUsing(question)"
                  @click="removeQuestionFromQuestionSet(question)"
                  class="bx bx-minus font-size-24 text-danger hand"
                  v-b-tooltip.hover
                  :title="$t('question-set.remove-question')"
                ></i>
                <i
                  v-else
                  @click="addQuestionToQuestionSet(question)"
                  class="bx bx-plus font-size-24 text-primary hand"
                  v-b-tooltip.hover
                  :title="$t('question-set.add-question')"
                ></i>

                <div class="modal-text-container">
                  <div>
                    {{ question.products && question.products.map(p => $t("product." + p.toLowerCase() + ".text")).join(", ") }}
                  </div>
                  <div class="text mb-2">
                    <i @click="goToQuestionEdit(question)" class="bx bx-pencil text-warning hand" v-b-tooltip.hover :title="$t('common.edit')"></i>

                    {{ question.name | translate }}
                  </div>

                  <div class="pt-2 ml-2"><question-category-select :label="$t('question.category.fwsed')" :selected="question.fwsedCategory" :disabled="true" /></div>
                  <div class="pt-2 ml-2 text-main" v-if="question.fileRequired">{{ $t("question.file-required") }}</div>

                  <div class="answers">
                    <ol>
                      <li v-for="option in question.options" :key="option._id">
                        {{ option.name | translate }}
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              <!-- Begin: Pagination -->

              <div class="row">
                <div class="col">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="libraryRequest.options.page"
                      :total-rows="libraryPagination.totalDocs"
                      :per-page="libraryPagination.limit"
                      limit="10"
                      size="sm"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
              <!-- End: Pagination -->
            </div>

            <div class="d-flex mt-4 mx-auto" style="gap: 0.75rem; width: max-content;">
              <button class="button cancel-button flex-shrink-1" @click="questionModal = false">
                {{ $t("common.cancel") }}
              </button>
            </div>
          </div>

          <div :class="{ active: activeModalTab === 'newquestion' }">
            <div class="mb-2">
              <b-form-tags v-model="questionForm.products" size="lg" class="mb-2" add-on-change no-outer-focus>
                <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                  <b-form-select v-bind="inputAttrs" v-on="inputHandlers" :disabled="disabled || !scopedProducts || scopedProducts.length === 0" :options="localizedScopedProducts">
                    <template #first>
                      <!-- This is required to prevent bugs with Safari -->
                      <option disabled value=""> {{ $t("product.text") }}</option>
                    </template>
                  </b-form-select>

                  <ul v-if="tags.length > 0" class="list-inline d-inline-block mt-2">
                    <li v-for="tag in tags" :key="tag" class="list-inline-item">
                      <b-form-tag @remove="removeTag(tag)" :title="tag" :disabled="disabled" variant="primary">{{ $t("product." + tag.toLowerCase() + ".text") }}</b-form-tag>
                    </li>
                  </ul>
                  <b-form-text v-else>
                    <span class="text-danger">{{ $t("product.required") }}</span>
                  </b-form-text>
                </template>
              </b-form-tags>

              <div class="mt-3">
                <sweet-input
                  v-model="questionForm.name"
                  :isValid="!$v.questionForm.name.$error"
                  @input="$v.questionForm.name.$touch()"
                  layout="vertical"
                  :label="$t('question.content')"
                  class="mb-3"
                />
              </div>

              <div class="d-flex flex-row mb-3">
                <div class="mt-2">
                  <h6 class="mt-2">{{ $t("question.category.fwsed") }}</h6>
                </div>
                <div class="pt-3 ml-2"><question-category-select :label="$t('question.category.fwsed')" :selected="questionForm.fwsedCategory" @onSelect="onCategorySelect" /></div>
              </div>

              <h6 class="mt-3">ilişkili Tedbirler</h6>
              <filter-select
                key="questionFilter"
                id="questionFilter"
                ref="questionFilter"
                placeholder="İlişkili Tedbirler"
                :selected="questionForm.policyDefinitions"
                :multiple="true"
                @filter="onPolicyDefinitionSearch"
                @changed="onPolicyDefinitionSelect"
              />

              <div class="d-flex flex-col flex-grow-1 mt-3">
                <h6>{{ $t("question.answer-type.text") }}</h6>

                <select v-model="questionForm.answerType" class="form-control">
                  <option value="SINGLE">{{ $t("question.answer-type.single") }}</option>
                  <option value="MULTIPLE">{{ $t("question.answer-type.multiple") }}</option>
                  <option value="FREE_TEXT">{{ $t("question.answer-type.free-text") }}</option>
                </select>
              </div>
              <div class="mt-3">
                <b-form-checkbox v-model="questionForm.fileRequired" switch>
                  {{ $t("question.file-required") }}
                </b-form-checkbox>
                <small class="d-block mt-1">{{ $t("question.file-required-help") }}</small>
              </div>
            </div>

            <div v-if="questionForm.options.length > 0">
              <div class="added-questions" v-for="x in questionForm.options" :key="x._id">
                <div class="d-flex align-items-center">
                  <div class="pcard--actions">
                    <i class="iconoir-edit" @click="editOption(x)"></i>
                    <i class="iconoir-trash" @click="removeOption(x._id)"></i>
                  </div>
                  <h5 class="ml-4">
                    {{ x.name }}
                  </h5>
                </div>
              </div>
              <hr />
            </div>

            <span v-if="questionForm.answerType != 'FREE_TEXT' && !addNewOptionPanel" @click="addNewOptionPanel = true" class="hand text-primary">
              <i class="bx bx-plus"></i>
              {{ $t("question.add-option-panel") }}
            </span>

            <div class="modal-question-card" v-if="addNewOptionPanel">
              <div class="input-container w-100">
                <sweet-input v-model="optionName" :isValid="!$v.optionName.$error" @input="$v.optionName.$touch()" layout="vertical" label="Seçenek Metni" style="width: 100%;" />
              </div>
            </div>
            <div class="d-flex flex-wrap  justify-content-between">
              <b-button v-if="optionName" variant="main" @click="addOption()">{{ $t("question.add-option") }}</b-button>
              <b-button v-if="optionId" variant="secondary" @click="clearOptionForm()">{{ $t("question.cancel-edit") }}</b-button>
            </div>

            <div class="d-flex mt-4 mx-auto" style="gap: 0.75rem; width: max-content;">
              <button class="button cancel-button flex-shrink-0" @click="questionModal = false">
                {{ $t("common.cancel") }}
              </button>
              <button
                v-if="questionForm.name && (questionForm.answerType == 'FREE_TEXT' || (questionForm.options && questionForm.options.length > 0))"
                @click="saveQuestion"
                class="button save-button flex-shrink-0"
                style="width: auto; padding: 0 1.2rem;"
              >
                {{ $t("common.save") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LibraryQuestionChannel } from "@/helpers/broadcastChannel";
import SweetTextarea from "@c/form/sweet-textarea";
import { required } from "vuelidate/lib/validators";

import QuestionCategorySelect from "@cc/question-category-select.vue";

const state = "managementQuestionSet";
export default {
  components: { SweetTextarea, QuestionCategorySelect },
  data() {
    return {
      form: {
        products: [],
        name: undefined,
        description: undefined,
        ratingTable: [],
        isScoringActive: false,
        isRatingActive: false,
        questions: [],
      },

      questionForm: {
        name: undefined,
        answerType: "SINGLE",
        fwsedCategory: "TEST",
        options: [],
        policyDefinitions: [],
      },
      optionName: undefined,
      optionId: undefined,

      libraryRequest: {
        options: { page: 1, limit: 20 },
        query: { "scope.value": { value: "system", isObject: true } },
      },

      activeModalTab: "library",
      questionModal: false,
      addNewOptionPanel: false,
    };
  },
  validations: {
    form: {
      name: {
        required,
      },
      products: {
        required,
      },
      description: { required },
    },

    questionForm: {
      name: { required },
      options: { required },
      fwsedCategory: { required },
    },
    optionName: { required },
  },

  computed: {
    localizedScopedProducts() {
      if (!this.scopedProducts) return [];
      return this.scopedProducts.map((p) => {
        const val = typeof p === "string" ? p : p.value;
        return { value: val, text: this.$t("product." + val.toLowerCase() + ".text") };
      });
    },
    libraryPagination() {
      return this.$store.getters[`managementQuestion/pagination`];
    },
    isLibraryLoading() {
      return this.$store.getters[`managementQuestion/isLoading`];
    },
    isLoading() {
      return this.$store.getters[`managementQuestionSet/isLoading`];
    },
    totalQuestionPoint() {
      return this.form?.questions?.reduce((prev, wrapper) => {
        if (wrapper.question.answerType == "SINGLE") {
          return prev + Math.max(...wrapper.question.options.flatMap((i) => i.score ?? 0));
        }

        if (wrapper.question.answerType == "FREE_TEXT") {
          return prev;
        }

        const children = wrapper.question.options?.reduce((p, c) => p + Number(c.score ?? 0), 0);
        return prev + (children ?? 0);
      }, 0);
    },
    isSaveButtonEnabled() {
      if (this.form?.questions?.length < 1) return false;
      if (this.$v.form.$invalid) return false;

      return true;
    },
  },
  watch: {
    form: {
      deep: true,
      immediate: true,
      handler(v) {
        this.$v.$touch();
      },
    },
    libraryRequest: {
      deep: true,
      handler() {
        this.fetchLibraryItems();
      },
    },

    questionModal(v) {
      if (!v) {
        this.clearOptionForm();
        this.questionForm = {
          name: undefined,
          answerType: "SINGLE",
          options: [],
          policyDefinitions: [],
          fileRequired: false,
          fwsedCategory: "TEST",
        };
      }
    },

    "form.products": {
      handler() {
        this.fetchLibraryItems();
      },
    },
  },
  mounted() {
    if (this.$route.params.id) {
      this.fetchDetail();
    } else {
      this.fetchLibraryItems();
    }

    LibraryQuestionChannel.onmessage = (event) => {
      if (event.data?.type === "library-question-updated") {
        this.reloadQuestion(event.data.payload._id);
      }
    };
  },
  beforeDestroy() {
    LibraryQuestionChannel.onmessage = null;
  },
  methods: {
    async onPolicyDefinitionSearch(value, callback) {
      const response = await this.$store.dispatch(`managementPolicy/paginate`, {
        options: { limit: 200 },
        query: { "scope.value": { value: "system", isObject: true }, name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onPolicyDefinitionSelect(value) {
      this.questionForm.policyDefinitions = value;
    },
    onCategorySelect(item) {
      this.questionForm = { ...this.questionForm, fwsedCategory: item };
    },
    async reloadQuestion(id) {
      const { data } = await this.$store.dispatch("managementQuestion/findById", id);
      if (data) {
        this.form.questions.forEach((i) => {
          if (i.question._id == data._id) {
            i.question = data;
          }
        });
      }
    },
    getDimensionText(key) {
      if (key == "PRIVACY") {
        return "Gizlilik";
      } else if (key == "ENTIRETY") {
        return "Bütünlük";
      } else if (key == "ACCESSIBILITY") {
        return "Erişebilirlik";
      } else if (key == "EFFECTED_PEOPLE_COUNT") {
        return "Etkilenen Kişi Sayısı";
      } else if (key == "SOCIAL_RESULTS") {
        return "Toplumsal Sonuçllar";
      } else if (key == "CORPORATE_RESULTS") {
        return "Kurumsal Sonuçlar";
      } else if (key == "SECTORAL_EFFECT") {
        return "Sektörel Etki";
      } else if (key == "DEPENDENT_ENTITIES") {
        return "Bağımlı Varlıklar";
      }
    },

    async fetchDetail() {
      const id = this.$route.params.id;
      try {
        const { data } = await this.$store.dispatch("managementQuestionSet/findById", id);
        this.form = {
          _id: data._id,
          products: data.products,
          name: data.name[this.currentLanguage],
          description: data.description[this.currentLanguage],
          ratingTable: data.ratingTable?.map((i) => ({ name: i.name ? i.name[this.currentLanguage] : "", min: i.min, max: i.max })),
          isScoringActive: data.isScoringActive,
          isRatingActive: data.isRatingActive,
          questions:
            data.questions
              ?.map((wrapper) => {
                if (!wrapper.question) {
                  return null;
                }

                if (data.isScoringActive) {
                  wrapper.question.options = wrapper.question.options.map((option) => {
                    return { ...option, score: wrapper.optionScores.find((w) => w.optionId == option._id)?.score };
                  });
                }

                return { ...wrapper };
              })
              .filter((i) => !!i) ?? [],
        };
      } catch (error) {
        console.error(error);
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    addOption() {
      if (!this.optionId) {
        this.optionId = "id" + new Date().getTime();
      }
      this.questionForm.options = [
        ...this.questionForm.options.filter((i) => i._id != this.optionId),
        {
          _id: this.optionId,
          name: this.optionName,
        },
      ];

      this.clearOptionForm();
    },
    removeOption(id) {
      this.questionForm.options = [...this.questionForm.options.filter((i) => i._id != id)];
    },
    editOption(option) {
      this.optionName = option.name;
      this.optionId = option._id;

      this.addNewOptionPanel = true;
    },

    clearOptionForm() {
      this.optionName = undefined;
      this.optionId = undefined;
      this.addNewOptionPanel = false;
    },

    async saveQuestion() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }

      const questionForm = {
        ...this.questionForm,
        options: this.questionForm.options.map((o) => {
          return {
            ...o,
            name: {
              [this.currentLanguage]: o.name,
            },
          };
        }),
      };

      try {
        const { data: question } = await this.$store.dispatch(`managementQuestion/save`, questionForm);

        this.addQuestionToQuestionSet(question);

        // this.form = {
        //   ...this.form,
        //   questions: [...this.form.questions, { question: { ...question }, score: 0, optionScores: [], order: Math.max(this.form.questions.map((i) => i.order)) + 1 }].sort(
        //     (a, b) => a.order - b.order
        //   ),
        // };
        this.$toast.success(this.$t("common.success"));
        this.questionModal = false;
        this.fetchLibraryItems();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    addConditionalQuestion(wrpr) {
      const wrapper = this.form.questions.find((i) => i.question._id == wrpr.question._id);
      if (!wrapper.conditionalQuestions) {
        wrapper.conditionalQuestions = [];
      }

      if (wrapper.conditionalQuestions?.find((i) => i.optionId == wrpr.conditionalQuestionOptionChoose && i.questionId == wrpr.conditionalQuestionOtherQuestionChoose)) {
        return;
      }
      wrapper.conditionalQuestions.push({
        optionId: wrpr.conditionalQuestionOptionChoose,
        questionId: wrpr.conditionalQuestionOtherQuestionChoose,
      });

      wrpr.conditionalQuestionOptionChoose = undefined;
      wrpr.conditionalQuestionOtherQuestionChoose = undefined;
    },
    renderConditionals(wrapper) {
      if (!wrapper.conditionalQuestions) return [];
      return wrapper.conditionalQuestions
        .map((i) => {
          i.option = wrapper.question.options.find((o) => o._id == i.optionId);
          i.question = this.form.questions.find((q) => q.question._id == i.questionId)?.question;

          return i;
        })
        .filter((i) => !!i.question);
    },
    removeConditional(wrpr, conditional) {
      const wrapper = this.form.questions.find((i) => i.question._id == wrpr.question._id);
      if (!wrapper) {
        return;
      }
      wrapper.conditionalQuestions = wrapper.conditionalQuestions?.filter((i) => !(i.optionId == conditional.optionId && i.questionId == conditional.questionId));
    },
    //

    goToQuestionEdit(question) {
      const href = "/management/question/?id=" + question._id;
      window.open(href, "_blank");
    },

    isQuestionUsing(question) {
      return this.form.questions.find((i) => i.question._id == question._id);
    },
    addQuestionToQuestionSet(question) {
      this.form = {
        ...this.form,
        questions: [...(this.form?.questions || []), { question: { ...question }, score: 0, optionScores: [], order: Math.max(this.form?.questions.map((i) => i.order)) + 1 }].sort(
          (a, b) => a.order - b.order,
        ),
      };
      this.form.questions.forEach((question, index) => (question.order = index + 1));
    },
    removeQuestionFromQuestionSet(question) {
      this.form = {
        ...this.form,
        questions: [...this.form.questions.filter((i) => i.question._id != question._id)].sort((a, b) => a.order - b.order),
      };
      this.form.questions.forEach((question, index) => (question.order = index + 1));
    },
    fetchLibraryItems() {
      this.libraryRequest.query.$and = [];

      if (this.form.products?.length > 0) {
        this.libraryRequest.query.$and.push({
          $or: [...this.form.products.map((i) => ({ products: i }))],
        });
      } else {
        this.libraryRequest.query.$and.push({
          $or: [{ products: [] }],
        });
      }

      if (this.libraryRequest.query.$and.length == 0) {
        delete this.libraryRequest.query.$and;
      }
      this.$store.dispatch(`managementQuestion/paginate`, this.libraryRequest);
    },

    orderUp(question) {
      if (question.order == 1) {
        return;
      }
      const order = question.order ?? Math.max(this.form.questions.map((i) => i.order)) + 1;
      const founded = this.form.questions.find((i) => i.order == order - 1);
      question.order = founded.order;
      founded.order = order;

      this.form.questions.sort((a, b) => a.order - b.order);
      this.form.questions.forEach((question, index) => (question.order = index + 1));
    },
    orderDown(question) {
      if (question.order == this.form.questions.length + 1) {
        return;
      }
      const order = question.order ?? Math.max(this.form.questions.map((i) => i.order)) + 1;
      const founded = this.form.questions.find((i) => i.order > order);
      question.order = founded.order;
      founded.order = order;

      this.form.questions.sort((a, b) => a.order - b.order);
      this.form.questions.forEach((question, index) => (question.order = index + 1));
    },
    totalQuestionOptionScore(wrapper) {
      if (wrapper.question.answerType == "SINGLE") {
        return Math.max(...wrapper.question.options.flatMap((i) => Number(i.score || 0) ?? 0));
      }

      if (wrapper.question.answerType == "FREE_TEXT") {
        return 0;
      }

      return wrapper.question.options.reduce((prev, current) => prev + Number(current.score ?? 0), 0) ?? 0;
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }

      try {
        if (this.form.isScoringActive && this.totalQuestionPoint > 100) {
          return this.$toast.warning(this.$t("question-set.total-score-warning"));
        }

        const form = {
          ...this.form,
          questions: this.form.questions.map((wr) => {
            return {
              ...wr,
              optionScores: wr.question.options.map((i) => ({ optionId: i._id, score: Number(i.score ?? 0) })),
              conditionalQuestions: wr.conditionalQuestions?.filter((i) => !!this.form.questions.find((q) => q.question._id == i.questionId)?.question),
            };
          }),
          ratingTable: this.form?.ratingTable?.map((i) => ({ ...i, name: { [this.currentLanguage]: i.name } })),
        };

        await this.$store.dispatch(`${state}/save`, form);

        this.$toast.success(this.$t("common.success"));
        this.$router.push("/management/question-set");
        this.resetForm();
      } catch (error) {
        console.error(error);

        this.$handleHttpErrorByStatusCode(error);
      }
    },

    appendRating() {
      const table = this.form.ratingTable ?? [];
      this.form = {
        ...this.form,
        ratingTable: [...table, { name: undefined, min: 0, max: 0, id: "id" + new Date().getTime() }],
      };
    },
    removeRating(i) {
      const table = [...(this.form.ratingTable ?? [])];
      table.splice(i, 1);
      this.form = {
        ...this.form,
        ratingTable: [...table],
      };
    },

    resetForm() {
      this.form = {
        products: [],
        name: undefined,
        description: undefined,
        ratingTable: [{ name: undefined, min: 0, max: 0 }],
        isScoringActive: false,
        isRatingActive: false,
        questions: [],
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./question-set";
@import "./organization-risk-decision.scss";
</style>
<style>
.rd-card {
  width: 100% !important;
}
.question {
  align-items: flex-start;
  flex-wrap: wrap;
}
.question .text {
  word-break: break-word;
}
</style>
